import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { AiOutlineSend } from 'react-icons/ai';
import { MdAttachFile } from "react-icons/md";

const AgentChat = ({ moduleName = 'testcases', sessionId, setSessionId, preservedHistory = null, onSwitchModule, emailContext }) => {
  const [uploading, setUploading] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTransferCta, setShowTransferCta] = useState(false);
  const [intentDetected, setIntentDetected] = useState(null); // { moduleKey, niceName, pendingMessage }
  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // auto-resize textarea when input changes
  useEffect(() => {
    try {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.style.height = 'auto';
      const maxHeight = 160; // match inline maxHeight
      const newHeight = Math.min(ta.scrollHeight, maxHeight);
      ta.style.height = `${newHeight}px`;
    } catch (err) {
      // ignore
    }
  }, [input]);

  // Load recent history from the backend (last 20 messages) for the current session
  useEffect(() => {
    if (moduleName === 'emailAgent') return;
    let cancelled = false;
    async function loadHistory() {
      try {
        const apiBase = process.env.REACT_APP_API_URL || '';
        const url = `${apiBase}/api/ask/history${sessionId ? `?sessionId=${encodeURIComponent(sessionId)}` : ''}`;
        const res = await axios.get(url, { withCredentials: true });
        if (cancelled) return;
        const serverMessages = Array.isArray(res.data?.messages) ? res.data.messages : [];
        setMessages(serverMessages);
      } catch (err) {
        console.error('Failed to load history', err);
      }
    }
    loadHistory();
    return () => { cancelled = true; };
  }, [sessionId, moduleName]);

  // sendUserQuestion used by autoAsk after module switch restore
  async function sendUserQuestion(text) {
    if (!text) return;
    const userMessageLocal = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMessageLocal]);
    setLoading(true);
    try {
      const historyToSend = [...messages, userMessageLocal];
    const payload = { question: text, module: moduleName, history: historyToSend };
    if (emailContext && moduleName === 'emailAgent') payload.emailContext = emailContext;
    if (sessionId) payload.sessionId = sessionId;
    const res = await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/ask`, payload, {
      withCredentials: true,
    });
    if (res.data?.sessionId && setSessionId) {
      setSessionId(res.data.sessionId);
    }
      const assistantMessage = { role: 'assistant', content: res.data.answer };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      console.error(err);
      setMessages((prev) => [...prev, { role: 'assistant', content: '❌ Error: Could not get a response. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  }

  // Reset or restore chat history when the selected module changes
  useEffect(() => {
    console.log('[AgentChat] useEffect(moduleName,preservedHistory) fired', { moduleName, preservedHistory });
    const niceName = moduleName === 'testcases' ? 'Test Cases' : moduleName === 'plcChangeManagement' ? 'Change Management' : moduleName;
    if (preservedHistory && preservedHistory.module === moduleName && Array.isArray(preservedHistory.messages)) {
      // restore preserved messages
      setMessages(preservedHistory.messages || []);
      // if autoAsk is present, perform the ask automatically
      if (preservedHistory.autoAsk) {
        // small timeout to allow render
        setTimeout(() => {
          sendUserQuestion(preservedHistory.autoAsk);
        }, 400);
      }
    }
    // if no preserved history and no messages yet, show a small loaded message once (skip for email agent)
    setMessages((prev) => {
      if (prev.length > 0 || !moduleName || moduleName === 'emailAgent') return prev;
      return [{ role: 'assistant', content: `🔁 Loaded module: ${niceName}. Chat history reset.` }];
    });
    // clear any pending intent when module changes
    setIntentDetected(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleName, preservedHistory]);

  const handleQuickPrompt = async (text, opts = {}) => {
    if (!text || loading) return;
    setLoading(true);
    try {
      const historyToSend = [...messages, { role: 'user', content: text }];
      const payload = { question: text, module: moduleName, history: historyToSend };
      if (emailContext && moduleName === 'emailAgent') payload.emailContext = emailContext;
      if (sessionId) payload.sessionId = sessionId;
      const res = await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/ask`, payload, {
        withCredentials: true,
      });
      if (res.data?.sessionId && setSessionId) {
        setSessionId(res.data.sessionId);
      }
      const assistantMessage = { role: 'assistant', content: res.data.answer };
      setMessages((prev) => [...prev, assistantMessage]);
      if (typeof opts.onComplete === 'function') opts.onComplete();
    } catch (err) {
      console.error(err);
      setMessages((prev) => [...prev, { role: 'assistant', content: '❌ Error: Could not get a response. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  // sendUserQuestion is defined below; declare via function hoisting

  const handleSubmit = async (e) => {
    // allow calling without an event (programmatic submit)
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!input.trim()) return;
  // detect intent only when in General module (default)
  const text = input;
  const userMessage = { role: 'user', content: text };
  const detectIntent = (textParam) => {
    if (!text) return null;
    console.log('[AgentChat] detectIntent input=', textParam);
    const t = textParam.toLowerCase();
    // crude keyword-based detection
    const testKeywords = ['test case', 'testcase', 'testcases', 'uat', 'iso', 'funktionel test', 'funktionel', 'test'];
    const changeKeywords = ['change management', 'change', 'adkar', 'forandringsledelse', 'forandring', 'forandrings'];
    const ideaKeywords = ['idea', 'ideas', 'idea generation', 'ideation', 'brainstorm', 'brainstorming', 'idé', 'idéer', 'idéudvikling'];
    const scopingKeywords = ['scoping', 'scope', 'scoping phase', 'preliminary analysis', 'foranalyse', 'afgrænsning'];
    const usecaseKeywords = ['use case', 'use-case', 'usecase', 'user story', 'user stories', 'requirements', 'krav'];

    if (testKeywords.some((k) => t.includes(k))) return { moduleKey: 'testcases', niceName: 'Test Cases' };
    if (changeKeywords.some((k) => t.includes(k))) return { moduleKey: 'plcChangeManagement', niceName: 'Change Management' };
    if (ideaKeywords.some((k) => t.includes(k))) return { moduleKey: 'plcIdeaGeneration', niceName: 'PLC Idea generation' };
    if (scopingKeywords.some((k) => t.includes(k))) return { moduleKey: 'plcScopingAndPreliminaryAnalysis', niceName: 'PLC Scoping' };
    if (usecaseKeywords.some((k) => t.includes(k))) return { moduleKey: 'plcRequirementsAndUserStories', niceName: 'Use case writer' };
    return null;
  };

  // If we're in the General/default module, check for intent before sending
  if (moduleName === 'default') {
    const intent = detectIntent(text);
    if (intent) {
      // prompt for confirmation before switching. store pending message
      console.log('[AgentChat] intent detected ->', intent);
      setIntentDetected({ ...intent, pendingMessage: userMessage });
      setInput('');
      return;
    }
  }

  // Otherwise proceed to send the question
  // Optimistically add the user message to local UI
  setMessages((prev) => [...prev, userMessage]);
  setInput('');
  setLoading(true);

  try {
    // Send the full session history (local UI messages + current user message)
    const historyToSend = [...messages, userMessage];
    const payload = { question: text, module: moduleName, history: historyToSend };
    if (emailContext && moduleName === 'emailAgent') payload.emailContext = emailContext;
    if (sessionId) payload.sessionId = sessionId;
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL || ''}/api/ask`,
      payload,
      { withCredentials: true }
    );

    const assistantMessage = { role: 'assistant', content: res.data.answer };
    setMessages((prev) => [...prev, assistantMessage]);
  } catch (err) {
    console.error(err);
    setMessages((prev) => [...prev, { role: 'assistant', content: '❌ Error: Could not get a response. Please try again.' }]);
  } finally {
    setLoading(false);
  }
  };

  

  return (
    <div style={{
      width: '95%',
      margin: '1.5rem 0',
      padding: '1rem',
      borderRadius: '20px',
      background: 'linear-gradient(to bottom right, rgba(255, 255, 255, 0.94), rgba(251, 216, 216, 0.92))',
      boxShadow: '0 8px 20px rgba(192, 54, 38, 0.12)'
    }}>
      <style>{`
        .agent-chat-scroll {
          scrollbar-width: none;
          scrollbar-color: transparent transparent;
        }
        .agent-chat-scroll::-webkit-scrollbar {
          width: 0;
          height: 0;
        }
        .agent-chat-scroll::-webkit-scrollbar-thumb,
        .agent-chat-scroll::-webkit-scrollbar-track,
        .agent-chat-scroll::-webkit-scrollbar-button {
          display: none;
        }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <span style={{ fontWeight: 600, color: '#2A0D05' }}>
          Agent chat (model: gpt-4o-mini) {moduleName ? `· module: ${moduleName}` : ''}
        </span>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={() => setMessages([])}
            style={{
              background: 'none',
              border: 'none',
              color: '#C03626',
              fontSize: '0.8rem',
              cursor: 'pointer',
              opacity: 0.6,
              padding: '0.25rem 0.5rem',
              borderRadius: '6px',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.6; }}
          >
            Ryd chat
          </button>
        )}
      </div>

      {moduleName === 'emailAgent' && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <button
            type="button"
            style={{
              padding: '10px 14px',
              borderRadius: 10,
              border: '1px solid #E5C7C0',
              background: '#ffffff',
              color: '#2D1F1B',
              fontWeight: 600,
              cursor: 'pointer',
              flex: '0 0 auto',
            }}
            onClick={() => handleQuickPrompt('Write a short summary of the email (1-5 sentences) capturing the essence, what action is required from the user, and who it is from.')}
          >
            Summarize email
          </button>
          <button
            type="button"
            style={{
              padding: '10px 14px',
              borderRadius: 10,
              border: '1px solid #E5C7C0',
              background: '#ffffff',
              color: '#2D1F1B',
              fontWeight: 600,
              cursor: 'pointer',
              flex: '0 0 auto',
            }}
            onClick={() => handleQuickPrompt('Analyze this email. First confirm if user action is required. If yes, extract specific tasks directly from the email details (subject, sender, date/time, body). Quote or paraphrase the actual asks/feedback so each task is concrete and tied to the email. If no action is obvious, say so and ask what needs clarification. Keep it concise and specific to this email only.', { onComplete: () => setShowTransferCta(true) })}
          >
            Make tasks from email
          </button>
        </div>
      )}

      <div
        className="agent-chat-scroll"
        style={{
          height: '320px',
          overflowY: 'auto',
          padding: '0.75rem',
          borderRadius: 12,
          backgroundColor: 'transparent',
          backdropFilter: 'blur(2px)',
        }}
      >
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
            <div style={{
              maxWidth: '75%',
              backgroundColor: msg.role === 'user' ? '#FFE1D8' : '#FFF4F1',
              border: msg.role === 'user' ? '1px solid rgba(192, 54, 38, 0.18)' : '1px solid rgba(192, 54, 38, 0.12)',
              padding: '0.85rem 1rem',
              borderRadius: '14px',
              whiteSpace: 'pre-wrap',
              fontSize: '0.95rem',
              color: '#2A0D05',
              boxShadow: '0 6px 14px rgba(192, 54, 38, 0.06)',
            }}>
              <strong style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}>{msg.role === 'user' ? 'You' : 'Agent'}</strong>
              <div style={{ fontSize: '0.95rem' }}>{msg.content}</div>
            </div>
          </div>
        ))}
        {loading && <div style={{ color: '#888' }}>🤖 Agent is thinking…</div>}
        <div ref={chatEndRef} />
      </div>

      {showTransferCta && (
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            style={{
              padding: '10px 14px',
              borderRadius: 10,
              border: '1px solid #C03626',
              background: '#FFF4F1',
              color: '#C03626',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Transfer to To-Do
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Skriv dit spørgsmål her..."
          style={{
            flex: 1,
            padding: '0.75rem 0.9rem',
            border: '0px solid rgba(192, 54, 38, 0.25)',
            minHeight: 32,
            maxHeight: 160,
            overflowY: 'hidden',
            resize: 'none',
            fontFamily: 'inherit',
            backgroundColor: 'transparent',
            outline: 'none',
          }}
          onKeyDown={(e) => {
            // Common chat UX: Enter => send, Shift+Enter => newline
            if (e.key === 'Enter') {
              if (!e.shiftKey) {
                // Enter without Shift -> submit
                e.preventDefault();
                handleSubmit();
              }
              // else Shift+Enter -> allow default (newline)
            }
          }}
        />

        {/* Hidden file input and visible icon button to its left */}
        {/* uploads from chat default to company context; backend merges contexts for retrieval */}
        <input id="agent-file-input" type="file" style={{ display: 'none' }} onChange={async (e) => {
          const file = e.target.files && e.target.files[0];
          if (!file) return;

          setUploading(true);
          // Show an optimistic message in the chat
          setMessages((prev) => [...prev, { role: 'assistant', content: `Uploading ${file.name}...` }]);

          try {
            const form = new FormData();
            if (sessionId) form.append('sessionId', sessionId);
            form.append('file', file);
            // default chat uploads to 'company' (server will merge contexts when answering)
            form.append('contextType', 'company');

            const apiUrl = process.env.REACT_APP_API_URL || '';
            const res = await axios.post(`${apiUrl}/api/upload`, form, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (res.data) {
              if (res.data.sessionId && setSessionId) setSessionId(res.data.sessionId);
              if (res.data.ok) {
                setMessages((prev) => [...prev, { role: 'assistant', content: `✅ Uploaded ${file.name} (${res.data.added || 0} chunks)` }]);
                alert(`Uploaded. Added ${res.data.added || 0} chunks to session.`);
              } else if (res.data.ok === false) {
                setMessages((prev) => [...prev, { role: 'assistant', content: `❌ Upload failed for ${file.name}` }]);
                alert('Upload failed');
              }
            }
          } catch (err) {
            console.error('Upload error', err);
            setMessages((prev) => [...prev, { role: 'assistant', content: `❌ Upload error for ${file.name}` }]);
            alert('Upload failed');
          } finally {
            // clear the file input so the same file can be picked again if needed
            e.target.value = '';
            setUploading(false);
          }
        }} />

        <label htmlFor="agent-file-input" title="Attach file" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
          <MdAttachFile size={20} color="#333" />
        </label>

        {/* If intent detected in General module, show confirmation controls */}
        {intentDetected && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <div style={{ marginRight: 6, color: '#333' }}>Detected: {intentDetected.niceName}. Switch?</div>
            <button type="button" onClick={async () => {
              // confirm switch: preserve current messages + pendingMessage, and autoAsk the pending content in the new module
              if (onSwitchModule) {
                const hist = [...messages, intentDetected.pendingMessage];
                onSwitchModule(intentDetected.moduleKey, { preserveHistory: true, history: hist, autoAsk: intentDetected.pendingMessage.content });
              }
              setIntentDetected(null);
            }} style={{ padding: '6px 8px', borderRadius: 6, background: '#2e7d32', color: '#fff', border: 'none' }}>Confirm</button>
            <button
              type="button"
              onClick={() => {
                // cancel: keep current module and send pending message as-is
                const pending = intentDetected?.pendingMessage?.content;
                setIntentDetected(null);
                if (pending) {
                  sendUserQuestion(pending);
                }
              }}
              style={{ padding: '6px 8px', borderRadius: 6, background: '#ddd', border: 'none' }}
            >
              Cancel
            </button>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !!intentDetected}
          style={{
            padding: '0.75rem 1.1rem',
            borderRadius: 10,
            border: 'none',
            background: 'linear-gradient(135deg, #C03626, #FF6F61)',
            color: '#fff',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 18px rgba(192, 54, 38, 0.16)',
            height: 40,
          }}
        >
          <AiOutlineSend size={20} />
        </button>
      </form>
    </div>
  );
};

export default AgentChat;
